import React, { useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Typography } from "../../../common";
import { CheckCircleIcon, XCircleIcon, Sparkles } from "lucide-react";

interface DropZoneProps {
  id: string;
  label: string;
  matchedItem: string | null;
  isCorrect: boolean;
  isIncorrect: boolean;
  isLocked: boolean; // New prop to prevent further drops
  // eslint-disable-next-line no-unused-vars
  onDrop: (itemId: string) => void;
  feedback: {
    correct_title: string;
    correct_text: string;
    incorrect_title: string;
    incorrect_text: string;
  };
  correctFeedback?: string; // Individual feedback for this specific match
}

const DropZone: React.FC<DropZoneProps> = ({
  id,
  label,
  matchedItem,
  isCorrect,
  isIncorrect,
  isLocked,
  feedback,
  correctFeedback,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isOver } =
    useSortable({
      id,
      disabled: isLocked, // Disable sorting when locked
    });
  const perPairFeedback =
    typeof correctFeedback === "string" ? correctFeedback.trim() : "";

  useEffect(() => {
    if (isCorrect && !perPairFeedback) {
      // eslint-disable-next-line no-console
      console.warn(`Missing per-pair correct_feedback for match target ${id}`);
    }
  }, [id, isCorrect, perPairFeedback]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getBackgroundColor = () => {
    if (isCorrect) {return "bg-green-50 border-green-300 shadow-green-100";}
    if (isIncorrect) {return "bg-red-50 border-red-300 shadow-red-100";}
    if (isOver && !isLocked)
    {return "bg-blue-50 border-blue-300 shadow-blue-100";}
    return "bg-gray-50 border-gray-200 shadow-gray-100";
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="dnd-drop-zone"
      data-dnd-id={id}
      {...(isLocked ? {} : { ...attributes, ...listeners })}
      className={`min-h-[80px] border-2 border-dashed rounded-xl p-4 mb-4 transition-all duration-300 shadow-md ${getBackgroundColor()} ${
        isLocked ? "cursor-default" : "cursor-pointer"
      }`}
    >
      <div className="flex items-center justify-between">
        <Typography
          variant="p"
          weight="medium"
          className="font-medium text-gray-700"
        >
          {label}
        </Typography>
        {isCorrect && <CheckCircleIcon className="w-5 h-5 text-green-600" />}
        {isIncorrect && <XCircleIcon className="w-5 h-5 text-red-600" />}
      </div>

      {matchedItem && (
        <div className="mt-3">
          <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-4 py-3 rounded-lg inline-block shadow-md">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4 text-blue-200" />
              <Typography variant="small" className="text-white font-medium">
                {matchedItem}
              </Typography>
            </div>
          </div>
        </div>
      )}

      {isCorrect && (
        <div className="mt-4 p-4 bg-green-100 rounded-lg border border-green-200 shadow-sm">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <Typography
              variant="small"
              className="text-green-800 leading-relaxed"
            >
              {perPairFeedback || "Correct"}
            </Typography>
          </div>
        </div>
      )}

      {isIncorrect && (
        <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-200">
          <div className="flex items-center gap-2">
            <XCircleIcon className="w-4 h-4 text-red-600" />
            <Typography variant="small" className="text-red-700 font-medium">
              {feedback?.incorrect_title}: {feedback?.incorrect_text}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropZone;
